// UseReorder.js

import { useCallback } from "react";
import {
  clone,
  clamp,
  inBounds,
  remapIndexSameList,
} from "./Helpers";

/**
 * Centralizes drag/drop reordering and the related selection remapping.
 */
export function useReorder(setWorkingForm, setSelection) {
  const handleMovePage = useCallback(
    (fromIndex, hoverIndex) => {
      setWorkingForm((prev) => {
        const next = clone(prev);
        const arr = Array.isArray(next.pages) ? [...next.pages] : [];
        if (!inBounds(fromIndex, arr.length) || !inBounds(hoverIndex, arr.length)) return prev;

        const [moved] = arr.splice(fromIndex, 1);
        const target = clamp(hoverIndex, 0, arr.length);
        arr.splice(target, 0, moved);
        next.pages = arr;
        return next;
      });

      setSelection((sel) => {
        if (!sel) return sel;
        if (sel.kind === "page") {
          return {
            kind: "page",
            pageIndex: remapIndexSameList(sel.pageIndex, fromIndex, hoverIndex),
          };
        }
        if (sel.kind === "control") {
          return {
            ...sel,
            pageIndex: remapIndexSameList(sel.pageIndex, fromIndex, hoverIndex),
          };
        }
        return sel;
      });
    },
    [setWorkingForm, setSelection]
  );

  const handleMoveControl = useCallback(
    (fromPage, fromControl, toPage, hoverControl) => {
      setWorkingForm((prev) => {
        const next = clone(prev);
        const srcPage = next.pages?.[fromPage];
        const dstPage = next.pages?.[toPage];
        if (!srcPage || !dstPage) return prev;

        const src = Array.isArray(srcPage.controls) ? [...srcPage.controls] : [];
        const dst = fromPage === toPage
          ? src
          : Array.isArray(dstPage.controls) ? [...dstPage.controls] : [];

        if (!inBounds(fromControl, src.length)) return prev;
        if (!inBounds(hoverControl, fromPage === toPage ? src.length : dst.length)) return prev;

        const [moved] = src.splice(fromControl, 1);
        let target = fromPage === toPage ? hoverControl : hoverControl + 1;
        target = clamp(target, 0, dst.length);
        dst.splice(target, 0, moved);

        if (fromPage === toPage) {
          next.pages[fromPage] = { ...srcPage, controls: dst };
        } else {
          next.pages[fromPage] = { ...srcPage, controls: src };
          next.pages[toPage]   = { ...dstPage, controls: dst };
        }
        return next;
      });

      setSelection((sel) => {
        if (!sel) return sel;

        if (fromPage === toPage) {
          if (sel.kind === "control" && sel.pageIndex === fromPage) {
            if (sel.controlIndex === fromControl) {
              return { kind: "control", pageIndex: toPage, controlIndex: hoverControl };
            }
            return {
              ...sel,
              controlIndex: remapIndexSameList(sel.controlIndex, fromControl, hoverControl),
            };
          }
          return sel;
        }

        const destIndex = hoverControl + 1;

        if (sel.kind === "control") {
          if (sel.pageIndex === fromPage && sel.controlIndex === fromControl) {
            return { kind: "control", pageIndex: toPage, controlIndex: destIndex };
          }
          if (sel.pageIndex === fromPage && sel.controlIndex > fromControl) {
            return { ...sel, controlIndex: sel.controlIndex - 1 };
          }
          if (sel.pageIndex === toPage && sel.controlIndex >= destIndex) {
            return { ...sel, controlIndex: sel.controlIndex + 1 };
          }
        }
        return sel;
      });
    },
    [setWorkingForm, setSelection]
  );

  return { handleMovePage, handleMoveControl };
}
